const blogPosts = [
  {
    slug: 'introducing-sos-a-lifeline-that-knows-its-a-lifeline',
    title: "Introducing SOS: A Lifeline That Knows It's a Lifeline",
    date: 'April 23, 2026',
    readTime: '5 min read',
    excerpt:
      "Most emergency features in consumer apps are panic buttons — you press, something happens to a list you might not have checked in years. SOS in 10thirty is different. The three people you choose have to choose you back. When you need them, the message goes to people who already know they're on your list.",
    hero:
      "Emergencies are usually when messaging apps fail you most. The person you most need is buried in a thread you haven't touched in weeks. You are typing at ten percent battery, your hands are shaking, and the one useful number is one contact search away — except search takes both hands. SOS in 10thirty is a feature we'd rather nobody ever needed. It exists for the moment when every friction cost matters.",
    sections: [
      {
        heading: 'Panic Buttons Miss the Point',
        paragraphs: [
          'Most emergency features in consumer apps are panic buttons. You press, and something happens to some list somewhere — a call to a national number, a text to your device\'s "emergency contacts", a notification that may or may not reach anyone you actually know.',
          "The failure mode is obvious the moment you think about it. The people who could actually help you in an emergency are the people you trust — not a default list your phone suggested when you set up the OS. A panic button that reaches strangers isn't a lifeline. It's a formality.",
        ],
      },
      {
        heading: "Your Lifeline Should Know It's Your Lifeline",
        paragraphs: [
          "The first design decision for SOS was consent. You pick up to three people. Each one has to accept the pairing. They see that you've asked them to be on your emergency list, and they decide whether to agree.",
          "That sounds like small friction, and it is — but it's the friction that matters. The people you pair with aren't just names in a field. They've been asked, and they've said yes. When your alert lands in their phone at 2am, they know immediately what it is and why it is from you. There is no \"who is this?\" in the middle of an emergency.",
        ],
      },
      {
        heading: 'Three Is a Human Number',
        paragraphs: [
          'We capped it at three. Not five, not ten, not unlimited. Three people.',
          "The reasoning is simple. If you have to think about which twelve people you'd alert in a crisis, you are not picking your lifeline — you are building a distribution list. Three forces the question back to the human level. Who would actually come? Who would actually pick up? Who, if you only had one message to send, would you send it to? And then two more. That is your three.",
        ],
      },
      {
        heading: 'A Signal That Expires',
        paragraphs: [
          "The alert itself disappears when it is read. Each recipient gets their own encrypted copy. When they mark it seen, their copy is gone, and you see the count tick down — two of three, one of three, zero. When it is zero, the whole thing is gone from the system.",
          "Your worst moments should not outlive them. There is no permanent record of a 3am SOS on our servers, no thread full of panicked messages to be reread later. The signal exists for as long as it is useful. Then it dissolves, the same way a conversation does.",
        ],
      },
      {
        heading: 'Location, Only When You Ask',
        paragraphs: [
          "SOS can include your location, but only if you have turned it on in settings. It is off by default. When it is on, every emergency alert carries an encrypted set of coordinates that your paired contacts can see.",
          "We made it opt-in because location data is the most sensitive thing a phone knows. If you want your family to know where you are when you SOS them, great — turn it on. If you do not, your alert still reaches them, still means something, still has their attention. The choice is yours, and it is a permanent setting, not a per-message decision you have to make in a panic.",
        ],
      },
      {
        heading: 'The Stand-Down Matters',
        paragraphs: [
          "Every panic has an after. The moment after you sent the alert, when the crisis resolves — maybe the turbulence stopped, maybe you found the route home, maybe you just realised the sound outside was a cat. Your three contacts are now worried, and they need to know it is okay.",
          "That is why the \"I'm fine\" follow-up exists. One tap sends an all-clear message to the same three people, with no cooldown and separate from the emergency rate limit. The system is designed to resolve the moment it started. Alerting someone and then leaving them to wonder is its own form of harm, and a good SOS feature acknowledges that.",
        ],
      },
      {
        heading: 'Not a Replacement, a Layer',
        paragraphs: [
          "10thirty SOS is not a 911 call. It is not a panic button wired to a security company. It sits between \"text my husband\" and \"call emergency services\" — the space where you want the people who love you to know, right now, that something is wrong, and to know before you have the presence of mind to explain.",
          "That space existed before SOS and it was filled badly — by draft messages you never sent, by group chats you did not want to burden, by trying to find one name in a thousand-person address book with trembling hands. Now it is one tap, to three people who know they are your people. We hope you never need it. We built it so you would have it if you did.",
        ],
      },
    ],
  },
  {
    slug: 'introducing-pigeons-when-the-message-is-enough',
    title: 'Introducing Pigeons: When the Message Is Enough',
    date: 'April 23, 2026',
    readTime: '5 min read',
    excerpt:
      'Not every message needs a reply. Sometimes you just want three people to know you landed safely, or a small group to see the same update before they keep walking. Pigeons carry a single message to a named group and disappear when it arrives. No thread. No group chat.',
    hero:
      "Every feature in 10thirty started with a question about what a conversation actually is — and what it isn't. Tunnels answered the first half: when two people want to be present with each other, we give them a room, and when the room closes it is gone. Pigeons answer the other half. Sometimes you do not want a conversation. You just want a message to land.",
    sections: [
      {
        heading: 'When a Tunnel Is Too Much',
        paragraphs: [
          'A tunnel requires both people to show up. That mutual commitment is what makes it feel different from ordinary messaging — both of you are deliberately present, both of you are accountable to the moment.',
          'But not every message carries that weight. A mother wanting her children to know she got home safely is not opening a conversation. She is delivering one piece of information, once. Forcing that into a tunnel would make every casual "arrived safe" feel like an appointment.',
        ],
      },
      {
        heading: 'The Group Chat Tax',
        paragraphs: [
          'Group chats are how most apps solve this, and they come with a tax most people do not examine. Every message becomes a permanent line in a permanent thread. Every "got home safe" lives forever. Every reaction, every "ok", every off-topic aside accumulates until the thread is a ledger of small decisions.',
          'That ledger changes behavior. People perform in group chats in ways they do not in private conversations. They edit themselves. They think about who might screenshot. A group chat is a public transcript dressed up as a private conversation.',
        ],
      },
      {
        heading: 'The Broadcast List Problem',
        paragraphs: [
          'Broadcast lists — the feature some apps offer where you can send one message to many without creating a group — feel closer to what we wanted. But they still live in the permanent archive model. The message stays in your sent folder. It stays in each recipient\'s inbox. It can be screenshotted, searched, referenced months later.',
          'We wanted something simpler. A message that goes to a trusted group, where each person gets their own private copy, each copy disappears when they have seen it, and then it is done. The shape is "I told them" — not "we had a conversation about this".',
        ],
      },
      {
        heading: 'Introducing Pigeons',
        paragraphs: [
          'A pigeon is a single message you send to a Nest. Each recipient receives their own end-to-end encrypted copy. When they read it, their copy disappears. You can see how many have read it as the count ticks down.',
          'When every recipient has read it, the pigeon is gone for everyone — not archived, not backed up, just over. If nobody reads it within 24 hours, it disappears anyway. A pigeon is never permanent. It is an act of telling, and like speech in a room, it carries weight only while it lasts.',
        ],
      },
      {
        heading: 'Why We Call Them Nests',
        paragraphs: [
          'Most apps treat groups as chat threads — shared rooms everyone lives in together. We treat them as audiences. A Nest is a saved circle of people you might want to tell something once. Your family. Your co-founders. Your climbing group. The five people who need to know when you are running late.',
          'We called them Nests because a Nest is where your pigeons come from. It is yours. The people in it cannot see who else is in it — a Nest is a private grouping, not a social venue. If a Nest no longer reflects who you trust with these small updates, you edit it or dissolve it. The relationships are in the people, not the thread.',
        ],
      },
      {
        heading: 'Knowing It Landed',
        paragraphs: [
          'One of the quiet anxieties of messaging is not knowing whether a message arrived as news or as noise. Pigeons answer this with a small, humane signal: the sender sees how many recipients have read it. Not when, not from whom — just the count going down as people see it.',
          'It is the minimum information needed to feel like the thing got through. Two of three read means two people know. When the counter empties, the pigeon is gone for everyone. You told them. They saw it. It is over.',
        ],
      },
      {
        heading: 'A Different Shape for Trust',
        paragraphs: [
          'Tunnels are for conversations — two people, fully present, for the length of the moment. Pigeons are for small, ephemeral announcements within circles you already trust. Between them, we think we have covered most of what people actually need to say to each other, without building yet another archive.',
          'Not every update needs a reply. Not every message needs to live forever. Some things — most things, really — are better delivered, acknowledged, and let go.',
        ],
      },
    ],
  },
  {
    slug: 'why-the-best-conversations-require-both-people-to-show-up',
    title: 'Why the Best Conversations Require Both People to Show Up',
    date: 'March 21, 2026',
    readTime: '6 min read',
    excerpt:
      'Most messaging lets you perform presence without actually being present. A tunnel does not. When someone opens one with you, you both have to be there. That changes everything about how it feels to talk.',
    hero:
      'There is a particular anxiety in modern messaging that nobody talks about directly. You type something that matters. You send it. And then you wait — not knowing if the other person saw it, is thinking about it, chose to ignore it, or simply has not looked at their phone. The message exists in a kind of limbo. You are present. They might not be.',
    sections: [
      {
        heading: 'Presence Without Structure Is Just Performance',
        paragraphs: [
          'Most messaging apps give you signals that someone is there — read receipts, typing indicators, last seen timestamps. These are band-aids. They tell you someone touched the app, not that they are actually with you.',
          'A read receipt means the message was opened. It says nothing about whether the person paused, actually read it, thought about it, or closed it immediately and moved on. The signal of presence and the reality of presence are not the same thing.',
        ],
      },
      {
        heading: 'The Asymmetry Problem',
        paragraphs: [
          'Conventional messaging is fundamentally asymmetric. One person can compose a careful, emotionally loaded message at midnight. The other person receives it at 7am between making coffee and getting on a train. The context in which it was written and the context in which it is read have nothing to do with each other.',
          'That asymmetry is not just inconvenient. It changes the quality of the exchange. The person who sent it has already moved through the emotion. The person who received it is just arriving at it. They are never quite in the same place at the same time.',
        ],
      },
      {
        heading: 'A Tunnel Is a Mutual Commitment',
        paragraphs: [
          'When someone opens a tunnel with you in 10thirty, something structurally different happens. They are not sending a message into your inbox to be dealt with whenever you get around to it. They are opening a window and choosing to be in it with you, right now.',
          'That choice means something. It is a small but real signal that they showed up — not passively, not by leaving a message on an answering machine, but by actually being present. You know they are there. They know you are there. The conversation is symmetrical by design.',
        ],
      },
      {
        heading: 'You Are Not Typing for Nothing',
        paragraphs: [
          'One of the underappreciated stresses of modern messaging is the uncertainty of attention. You do not know if what you are saying is landing. You do not know if the other person is reading as you type or will see it in three hours. You are essentially speaking into a void and hoping it reaches someone.',
          'A tunnel removes that uncertainty. When the window is open, both people are in it. What you say reaches someone who is present. That is not a small thing. It is the difference between a conversation and a note left on the kitchen table.',
        ],
      },
      {
        heading: 'Effort Signals Seriousness',
        paragraphs: [
          'The friction of opening a tunnel — the intentionality of it — also changes the weight of what happens inside it. People do not open tunnels by accident. They do not stumble into one mid-scroll. The act of initiating requires a small, deliberate decision.',
          'That decision is itself a signal. Someone choosing to have a conversation with you in 10thirty is choosing to be present with you specifically, in this moment. That is a different quality of attention than a message dashed off in a group chat notification.',
        ],
      },
      {
        heading: 'What Presence Actually Feels Like',
        paragraphs: [
          'The reason face-to-face conversation still feels irreplaceable is not only because you can see the other person. It is because you are both unambiguously there. Neither of you is somewhere else at the same time. The conversation has your full presence or it does not happen at all.',
          '10thirty cannot replicate that entirely. But it can bring digital messaging closer to it than anything built around the inbox model. When both people have to show up for the conversation to exist, showing up starts to mean something again.',
        ],
      },
    ],
  },
  {
    slug: 'introducing-10thirty-conversation-without-the-archive',
    title: 'Introducing 10thirty: Conversation Without the Archive',
    date: 'March 20, 2026',
    readTime: '6 min read',
    excerpt:
      'Modern messaging treats permanence as normal. 10thirty was built on a different assumption: some conversations should exist fully in the moment, then end.',
    hero:
      'Face-to-face conversation has a quality digital messaging lost. It belongs to the moment. You speak, the other person hears you, and when it ends, it ends. There is no archive waiting to be searched, screenshotted, or dragged into another context years later.',
    sections: [
      {
        heading: 'The Problem with Forever',
        paragraphs: [
          'Most messaging products are built to keep everything. They preserve the whole thread, index it, sync it, back it up, and quietly turn passing thoughts into durable records.',
          'That permanence does not just change storage. It changes behavior. People become less candid when they know every message might be reread, forwarded, subpoenaed, leaked, or surfaced long after the moment has passed.',
        ],
      },
      {
        heading: 'Conversation Used to End',
        paragraphs: [
          'In person, a conversation belongs to the room it happened in. You speak, you listen, you respond, and then the moment moves on. Memory remains, but the full record does not.',
          'That natural ending matters. It gives people room to be present instead of performative. It lets disagreement, vulnerability, and nuance exist without instantly becoming archive material.',
        ],
      },
      {
        heading: 'Privacy Is Not Suspicious',
        paragraphs: [
          'Privacy is not only for criminals, conspirators, or people with something to hide. It is for couples having difficult conversations. It is for journalists protecting sources. It is for lawyers, patients, friends, founders, families, and anyone who needs a little room around an honest exchange.',
          'A healthy society needs spaces where people can think out loud, speak carefully, change their minds, and trust that not every sentence will become permanent evidence of who they are.',
        ],
      },
      {
        heading: 'What 10thirty Does Differently',
        paragraphs: [
          '10thirty is built around tunnels: short, encrypted windows for conversation. A tunnel opens, two people connect, and the tunnel closes. Messages disappear after they are read. The conversation is designed to complete, not accumulate.',
          'That does not make conversation disposable. It makes it proportionate. Not every exchange deserves years of storage, backups, and metadata hanging off it forever.',
        ],
      },
      {
        heading: 'Less Stored Data, Less Future Risk',
        paragraphs: [
          'When sensitive communication is stored by default, it becomes breach material, discovery material, blackmail material, and contextless spectacle. The longer it survives, the more chances there are for it to be stolen, misunderstood, or used against the people involved.',
          '10thirty starts with a simpler principle: if a record does not need to outlive the moment, it should not be kept just because storage is cheap.',
        ],
      },
      {
        heading: 'A Different Default',
        paragraphs: [
          'We have spent years treating permanence as progress. But not every conversation becomes more valuable when it is stored forever. Some become less honest. Less human. Less free.',
          'Most apps are designed to preserve conversation. 10thirty is designed to complete it.',
        ],
      },
    ],
  },
  {
    slug: 'this-conversation-should-have-disappeared',
    title: 'This Conversation Should Have Disappeared',
    date: 'March 20, 2026',
    readTime: '8 min read',
    excerpt:
      'Modern scandal keeps proving the same point: when private communication lingers, it can become evidence, leverage, breach material, and attack surface.',
    hero:
      'Every private message creates a second life for a conversation. First there is the moment itself. Then there is the record: stored on a device, synced to a backup, retained by a service, exposed in discovery, stolen in a breach, or resurfaced years later in a context no one expected.',
    sections: [
      {
        heading: 'The Cost of Keeping Everything',
        paragraphs: [
          'The history of modern scandal is full of conversations that did not stay where they began. Sometimes they became courtroom exhibits. Sometimes they were hacked. Sometimes they were leaked, copied, forwarded, or simply kept long enough to be used for purposes no one imagined when they were sent.',
          'The point is not that every conversation should disappear. Some records should exist. Some accountability depends on them. But we have normalized the opposite extreme: everything kept, indefinitely, by default.',
        ],
      },
      {
        heading: '1. Boris Johnson and the UK Covid Inquiry',
        paragraphs: [
          'Private WhatsApp messages sent during a national emergency became part of a massive public inquiry and a political spectacle. What was once informal chat became evidence, context, and headline material.',
          'The lesson is not about one politician. It is about how quickly “private” messaging becomes part of the public record when the messages survive long enough to be demanded.',
        ],
      },
      {
        heading: '2. The Sony Pictures Hack',
        paragraphs: [
          'The 2014 Sony breach exposed years of internal email: executive arguments, salary details, private judgments, deal discussions, and embarrassing commentary that was never meant for public consumption.',
          'Stored communication became corporate attack surface. Once the archive existed, exfiltration turned private speech into reputational damage and strategic exposure.',
        ],
      },
      {
        heading: '3. Jeff Bezos and Persistent Messaging Risk',
        paragraphs: [
          'The 2019 revelations around Jeff Bezos and an allegedly malicious WhatsApp media file highlighted another angle: persistent messaging apps are not only archives, they are attack surfaces. Rich media, message storage, and durable device history create room for intrusion.',
          'The longer sensitive communication lives inside complex consumer messaging environments, the more chances there are for compromise.',
        ],
      },
      {
        heading: '4. Ashley Madison',
        paragraphs: [
          'The Ashley Madison breach exposed deeply personal user data and private communications at enormous scale. The harm was not hypothetical. Lives, relationships, and reputations were put at risk because intimate information had been stored centrally and kept long enough to be stolen.',
          'No security promise matters much once a large, durable store of sensitive conversation exists. The archive itself becomes the prize.',
        ],
      },
      {
        heading: '5. Journalists and Sources',
        paragraphs: [
          'For journalists, the danger is not only what a message says. It is that communication records can reveal who spoke to whom, when, and how often. That alone can expose a source.',
          'When sensitive exchanges leave detailed trails, both the content and the relationship become vulnerable to subpoenas, surveillance, and pressure.',
        ],
      },
      {
        heading: '6. COINTELPRO and the Logic of Surveillance',
        paragraphs: [
          'Long before modern smartphones, state surveillance campaigns showed how private communication can be intercepted, retained, and turned into leverage against legitimate dissent. The details of the technology change; the logic does not.',
          'If communication can be captured and stored, it can be repurposed. That is why privacy is not a luxury feature. It is part of political freedom.',
        ],
      },
      {
        heading: 'A Different Risk Profile',
        paragraphs: [
          '10thirty does not promise to erase all risk. No serious privacy product should. But it starts from a better question: does this conversation really need to outlive the moment that gave it meaning?',
          'The safest sensitive message is often the one that was never kept longer than necessary.',
        ],
      },
    ],
  },
  {
    slug: 'privacy-is-not-secrecy',
    title: 'Privacy Is Not Secrecy',
    date: 'March 20, 2026',
    readTime: '6 min read',
    excerpt:
      'The fastest way to misunderstand privacy is to assume it only belongs to people doing something wrong. Privacy is usually about dignity, context, and the ability to be human without an audience.',
    hero:
      'Whenever a privacy product appears, the same suspicion follows close behind: what exactly do people need this for? It is a revealing question, because it assumes that only wrongdoing deserves shelter. In reality, privacy is one of the conditions that makes honest life possible.',
    sections: [
      {
        heading: 'The Wrong Framing',
        paragraphs: [
          'People often talk about privacy as if it were a suspicious preference. If you want less tracking, less retention, or less visibility, the implication is that you must have something to hide.',
          'That framing confuses secrecy with privacy. Secrecy is about concealment from everyone. Privacy is about choosing the right boundaries for the right context. It is the difference between deception and discretion.',
        ],
      },
      {
        heading: 'Ordinary Life Needs Boundaries',
        paragraphs: [
          'Most privacy needs are not dramatic. They are small and everyday. A couple talking through a difficult moment. A patient messaging a doctor. A journalist protecting a source. A founder discussing a possible acquisition. A friend saying something vulnerable they are not ready to make permanent.',
          'None of that is criminal. None of it is exotic. It is just human life happening in a world that has made storage cheap and memory automatic.',
        ],
      },
      {
        heading: 'Context Is Part of Meaning',
        paragraphs: [
          'A sentence can be harmless in one moment and explosive in another. A joke, a half-formed thought, a disagreement, a fear, or a confession can change meaning completely when removed from its original context and resurfaced months or years later.',
          'Privacy protects context. It allows conversation to belong to the people and moment that produced it instead of being endlessly available for reinterpretation.',
        ],
      },
      {
        heading: 'The Freedom to Think Out Loud',
        paragraphs: [
          'People need places where they can think aloud, change their minds, ask naive questions, and speak imperfectly without feeling like every sentence is a permanent statement of record.',
          'Without that room, communication becomes flatter and more defensive. People stop being candid and start managing their future discoverability.',
        ],
      },
      {
        heading: 'Why This Matters for 10thirty',
        paragraphs: [
          '10thirty is built on the belief that privacy is not a niche feature for edge cases. It is part of what makes real conversation possible. The point is not to disappear accountability. It is to stop treating every ordinary exchange like it deserves indefinite retention.',
          'Privacy is not secrecy. It is human scale. It is the ability to speak inside a boundary that actually fits the moment.',
        ],
      },
    ],
  },
  {
    slug: 'why-permanent-chat-changes-how-people-speak',
    title: 'Why Permanent Chat Changes How People Speak',
    date: 'March 20, 2026',
    readTime: '7 min read',
    excerpt:
      'The archive is not passive. When people know a conversation will be saved forever, they speak differently. The medium quietly edits the human behavior inside it.',
    hero:
      'Permanent chat history is often sold as convenience. Searchability, continuity, memory, backup. What gets discussed less is the behavioral cost: once people know every message can live forever, they stop speaking only to the person in front of them and start speaking to future readers too.',
    sections: [
      {
        heading: 'The Archive Enters the Room',
        paragraphs: [
          'A persistent thread changes the shape of a conversation before anyone types a word. It introduces an invisible audience: your future self, the other person\'s future self, anyone with access to the device, anyone who might receive a forwarded screenshot, anyone reading the thread in discovery, anyone who stumbles across it later.',
          'Even when none of that happens, people intuit the possibility. They adapt preemptively.',
        ],
      },
      {
        heading: 'From Presence to Performance',
        paragraphs: [
          'In a face-to-face conversation, you are usually speaking to one person in one moment. In a permanent chat, you are often speaking to possible future interpreters as well. That shifts the tone from presence to performance.',
          'People hedge more. They flatten nuance. They avoid saying the difficult thing directly. They choose lines that will look acceptable later instead of lines that best express what is true now.',
        ],
      },
      {
        heading: 'Caution Is Not the Same as Honesty',
        paragraphs: [
          'There is a difference between thoughtfulness and self-censorship. Permanent records often encourage the second while pretending to encourage the first.',
          'The result is not better communication. It is safer-sounding communication. More polished. Less candid. More optimized for traceability than understanding.',
        ],
      },
      {
        heading: 'Relationships Feel It Too',
        paragraphs: [
          'This is not only a political or corporate problem. Personal relationships change under archival pressure too. Couples fight differently when every argument can be reopened. Friends disclose less when vulnerability feels permanently retrievable.',
          'The archive creates emotional drag. It becomes easier to store grievances than resolve them.',
        ],
      },
      {
        heading: 'A Different Design Choice',
        paragraphs: [
          '10thirty is built around the idea that conversation should not always harden into record. Bounded, disappearing chat creates different incentives. It brings people back toward the present instead of pushing them toward future defensibility.',
          'If the medium changes how people speak, then designing for impermanence is not cosmetic. It is behavioral design.',
        ],
      },
    ],
  },
  {
    slug: 'what-makes-a-tunnel-different-from-a-chat-thread',
    title: 'What Makes a Tunnel Different from a Chat Thread',
    date: 'March 20, 2026',
    readTime: '5 min read',
    excerpt:
      'A tunnel is not just a chat with a timer on it. It reflects a different assumption about how digital conversation should begin, unfold, and end.',
    hero:
      'Most messaging products are built around the endless thread. Once someone can reach you, the channel stays open. Messages pile up. The conversation can go dormant, flare up, fragment, drift, and return without ever really ending. A tunnel is built on a different model.',
    sections: [
      {
        heading: 'A Tunnel Begins With Intent',
        paragraphs: [
          'In 10thirty, a conversation does not begin because someone felt like typing into your life. It begins because a request is made and accepted. That small change matters.',
          'Intentionality turns access into something negotiated instead of assumed. It gives both people more control over when conversation happens and under what conditions.',
        ],
      },
      {
        heading: 'A Tunnel Is Time-Bound',
        paragraphs: [
          'A thread can run forever. A tunnel has edges. It opens, holds a conversation for a limited window, and then closes.',
          'Time bounds do not trivialize what is said inside. They make the space legible. You know when you are in it. You know it will not quietly sprawl into a background obligation.',
        ],
      },
      {
        heading: 'A Tunnel Leaves Less Behind',
        paragraphs: [
          'Persistent chat systems turn conversation into inventory. Threads become digital storage lockers for feelings, plans, conflicts, jokes, and fragments that may never need to exist again.',
          'A tunnel rejects that default. It is designed so the value stays in the exchange, not in the accumulation of records around it.',
        ],
      },
      {
        heading: 'A Tunnel Changes the Social Contract',
        paragraphs: [
          'An endless thread teaches people that they are always semi-available. A tunnel says something more precise: we are here now, for this, together.',
          'That makes the interaction feel closer to a conversation than a feed. It brings back a sense of presence, consent, and closure that many digital channels have worn down.',
        ],
      },
      {
        heading: 'Not Just a Feature, a Philosophy',
        paragraphs: [
          'The difference between a tunnel and a thread is not only technical. It is philosophical. One is designed to preserve access and history. The other is designed to make a moment possible, then let it end.',
          'That is the core of 10thirty: not more messaging, but a different idea of what messaging is for.',
        ],
      },
    ],
  },
  {
    slug: 'designing-for-the-end-of-a-conversation',
    title: 'Designing for the End of a Conversation',
    date: 'March 20, 2026',
    readTime: '6 min read',
    excerpt:
      'Most digital products are designed to keep interactions going. 10thirty starts from a stranger premise: a good conversation should be able to finish.',
    hero:
      'Most communication software is built to resist completion. Notifications pull you back. Threads stay open. Feeds refresh. The product is always asking for one more return. 10thirty starts from a stranger premise: a good conversation should be able to finish.',
    sections: [
      {
        heading: 'The Bias Toward Continuation',
        paragraphs: [
          'A product optimised for engagement has every reason to make endings feel unnatural. Unread badges preserve low-level tension. Drafts linger. History accumulates. Each open loop is a future re-entry the metrics depend on.',
          'The cost is borne by the user. Conversations that should have been finite become ambient. Attention that should have been gathered for a moment is spread thin across days. The exchange does not end so much as fade into background obligation.',
          'Human conversation does not always want this. Some exchanges are meaningful precisely because they are finite — they gather attention, do their work, and pass. Closure is not the opposite of connection. It is often what lets connection feel complete instead of draining.',
        ],
      },
      {
        heading: 'The Tunnel: Designing the Ending In',
        paragraphs: [
          'The tunnel is what happens when you take that idea seriously and build it into the product. A conversation opens between two people. It exists for a defined interval. Messages disappear as they are read. When the time is up, the space closes. Nothing carries over except the effect the conversation had on the people in it.',
          'That structure forces a different kind of attention. When the boundary is real, people focus. They say what matters. They stop performing for an archive that will never be reread. There is no thread to scroll back through later, no residue to manage, no half-finished exchange parked in an inbox waiting for a reply nobody quite wants to write.',
          'It also forces product discipline on us. We cannot lean on retention as a proxy for value. The question is never how to keep a user circulating — it is whether the tunnel helped them finish something that mattered. If it did, the conversation closing is the success state, not the failure state.',
          'This shape will not fit every use case, and it is not supposed to. It is built for the kinds of conversations that become worse the more surface area you give them.',
        ],
      },
      {
        heading: 'Completion as a Product Value',
        paragraphs: [
          'A lot of software quietly assumes more is better — more time, more content, more history, more touchpoints. 10thirty assumes that sometimes enough is enough, and that proportion is something a product can be designed for rather than something the user has to enforce against the grain of the tool.',
          'Designing for the end of a conversation is, in the end, designing for restraint on behalf of the person using the product. Give the moment the space it needs. Then let it be over.',
        ],
      },
    ],
  },
];

export function getAllBlogPosts() {
  return blogPosts;
}

export function getBlogPostBySlug(slug) {
  return blogPosts.find((post) => post.slug === slug);
}
